import { effectiveParentId, type LiveProject } from '@wiztime/core';
import { useMemo } from 'react';
import type { ProjectsView } from '../../hooks/useProjects';
import { useToday } from '../../hooks/useRouteView';
import { useSettings } from '../../hooks/useSettings';
import { useLastUsedDates } from './lastUsedDates';
import { buildProjectGroups, type ProjectGroup } from './projectGroups';

/**
 * `ordered` (selectable projects in the display order wanted) as project groups, with the fold
 * rule of `childFoldDays` applied. The last use of each project comes from all confirmed blocks
 * (one live query shared by all callers, `useLastUsedDates`).
 */
export function useProjectGroups(
  ordered: readonly LiveProject[],
  projects: ProjectsView,
): readonly ProjectGroup[] {
  const today = useToday();
  const settings = useSettings();
  const hasChildren = useMemo(
    () => ordered.some((project) => effectiveParentId(project, projects.byId) !== null),
    [ordered, projects.byId],
  );
  const lastUsed = useLastUsedDates(hasChildren);
  return useMemo(
    () =>
      buildProjectGroups(ordered, { byId: projects.byId, lastUsed, today, foldDays: settings.childFoldDays }),
    [ordered, projects.byId, lastUsed, today, settings.childFoldDays],
  );
}
